<template>
  <v-app>
    <v-container fluid class="loan-details-container">
      <v-row>
        <v-col cols="12">
          <v-card v-if="!hasValidId" elevation="1">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-information-outline</v-icon>
              <div class="text-h6 mb-2">No Loan Selected</div>
              <div class="text-body-2 text-medium-emphasis mb-4">
                Please select a loan from the list to view details.
              </div>
              <v-btn
                color="primary"
                variant="tonal"
                @click="router.push({ name: 'loan-records' })"
              >
                <v-icon start>mdi-format-list-bulleted</v-icon>
                Go to Loan Records
              </v-btn>
            </v-card-text>
          </v-card>

          <v-card v-else-if="loading" elevation="1">
            <v-card-text class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              />
              <div class="text-body-1 mt-4">Loading loan details...</div>
            </v-card-text>
          </v-card>

          <v-card v-else-if="error" elevation="1">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
              <div class="text-h6 mb-2 text-error">Error Loading Details</div>
              <div class="text-body-2 text-medium-emphasis mb-4">{{ error }}</div>
              <v-btn
                color="primary"
                variant="tonal"
                @click="fetchLoan"
                class="mr-2"
              >
                Retry
              </v-btn>
              <v-btn
                color="grey"
                variant="text"
                @click="router.push({ name: 'loan-records' })"
              >
                Back to List
              </v-btn>
            </v-card-text>
          </v-card>

          <template v-else-if="loan">
            <v-card elevation="1" class="mb-4">
              <v-card-title class="d-flex align-center pa-4">
                <v-icon class="me-2">mdi-account-cash</v-icon>
                Employee Loan Details
                <v-chip
                  :color="loan.status === 1 ? 'success' : 'grey'"
                  size="small"
                  class="ms-3"
                >
                  {{ loan.status === 1 ? 'Active' : 'Completed' }}
                </v-chip>
                <v-spacer />
                <v-btn
                  v-if="$updateAccess($route.fullPath) && canEdit"
                  color="warning"
                  variant="tonal"
                  size="small"
                  prepend-icon="mdi-pencil"
                  @click="router.push({ name: 'edit-loan', params: { id: loan.id } })"
                  class="me-2"
                >
                  Edit
                </v-btn>
                <v-btn
                  color="primary"
                  variant="tonal"
                  size="small"
                  prepend-icon="mdi-arrow-left"
                  @click="router.push({ name: 'loan-records' })"
                >
                  Back to Records
                </v-btn>
              </v-card-title>
            </v-card>

            <!-- Employee Information -->
            <v-card elevation="1" class="mb-4">
              <h2 class="section-title">Employee Information</h2>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Name:</strong> {{ loan.employee?.name || '-' }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Code:</strong> {{ loan.employee?.code || '-' }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Designation:</strong> {{ loan.employee?.designation?.name || '-' }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Department:</strong> {{ loan.employee?.department?.name || '-' }}</p>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Loan Information -->
            <v-card elevation="1" class="mb-4">
              <h2 class="section-title">Loan Information</h2>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Loan Amount:</strong> {{ formatNumber(loan.loan_amount) }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Total Installments:</strong> {{ loan.total_installments ?? loan.installments ?? '-' }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Total Paid:</strong> {{ formatNumber(totalPaid) }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Remaining:</strong> {{ formatNumber(remaining) }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Issue Date:</strong> {{ formatDisplayDate(loan.issue_date) }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Refund Start Date:</strong> {{ formatDisplayDate(loan.refund_start_date) }}</p>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Disbursement Voucher -->
            <v-card v-if="loan.voucher" elevation="1" class="mb-4">
              <h2 class="section-title">Disbursement Voucher</h2>
              <v-card-text>
                <v-row class="mb-4">
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Party Code:</strong> {{ loan.voucher.party?.code || '-' }}</p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="grid-item"><strong>Payment Account:</strong> {{ (loan.voucher.payment_account || loan.voucher.paymentAccount)?.name || '-' }}</p>
                  </v-col>
                </v-row>

                <v-table v-if="voucherItems.length > 0" density="compact" class="voucher-table">
                  <thead>
                    <tr>
                      <th>Account Head</th>
                      <th>Transaction</th>
                      <th>Amount</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in voucherItems" :key="idx">
                      <td>{{ getAccountHeadName(item) }}</td>
                      <td :class="getTransactionClass(item)">{{ getTransactionType(item) }}</td>
                      <td>{{ formatNumber(item.amount) }}</td>
                      <td>{{ item.remarks || '-' }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </v-card-text>
            </v-card>

            <!-- Approved Loan Payments -->
            <v-card elevation="1" class="mb-4">
              <h2 class="section-title">Approved Loan Payments</h2>
              <v-card-text>
                <v-table v-if="approvedPayments.length > 0" density="compact" class="payments-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, idx) in approvedPayments" :key="idx">
                      <td>{{ formatDisplayDate(p.payment_date) }}</td>
                      <td>{{ formatNumber(p.amount) }}</td>
                    </tr>
                  </tbody>
                </v-table>

                <v-alert v-else type="info" variant="tonal" density="compact" class="mt-2">
                  No approved payments yet.
                </v-alert>

                <div class="summary-box mt-4">
                  <strong>Total Paid:</strong> {{ formatNumber(totalPaid) }} <br />
                  <strong>Remaining:</strong> {{ formatNumber(remaining) }}
                </div>
              </v-card-text>
            </v-card>
          </template>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import formattedDate from "@/plugins/dateFormatter";

export default {
  name: "LoanDetailsPage",

  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const loan = ref(null);
    const loading = ref(false);
    const error = ref(null);

    const loanId = computed(() => route.params.id || route.query.id);

    const hasValidId = computed(() => !!loanId.value);

    const canEdit = computed(() => {
      if (!loan.value) return false;
      const paid = loan.value.paid_installments ?? 0;
      return paid === 0;
    });

    const approvedPayments = computed(() => loan.value?.approvedPayments || loan.value?.approved_payments || []);

    const voucherItems = computed(() => {
      const v = loan.value?.voucher;
      if (!v) return [];
      return v.voucherItems || v.voucher_items || [];
    });

    const totalPaid = computed(() =>
      approvedPayments.value.reduce((sum, p) => sum + Number(p.amount || 0), 0)
    );

    const remaining = computed(() =>
      (Number(loan.value?.loan_amount) || 0) - totalPaid.value
    );

    const formatDisplayDate = (date) => {
      if (!date) return "-";
      return formattedDate(date);
    };

    const formatNumber = (num) => {
      if (num == null) return "-";
      return Number(num).toLocaleString();
    };

    const getAccountHeadName = (item) => {
      const ah = item.account_head || item.accountHead;
      return ah?.name || "-";
    };

    const getTransactionType = (item) => {
      return (item.transaction_type || item.transactionType || "-").toUpperCase();
    };

    const getTransactionClass = (item) => {
      const t = (item.transaction_type || item.transactionType || "").toUpperCase();
      return t === "DEBIT" ? "debit" : t === "CREDIT" ? "credit" : "";
    };

    const fetchLoan = async () => {
      if (!loanId.value) return;
      loading.value = true;
      error.value = null;
      try {
        const data = await store.dispatch("getEmployeeLoanById", loanId.value);
        loan.value = data;
      } catch (e) {
        error.value = e?.response?.data?.message || "Failed to load loan details.";
      } finally {
        loading.value = false;
      }
    };

    watch(loanId, (val) => {
      if (val) fetchLoan();
      else loan.value = null;
    });

    onMounted(() => {
      if (loanId.value) fetchLoan();
    });

    return {
      loan,
      loading,
      error,
      hasValidId,
      canEdit,
      approvedPayments,
      voucherItems,
      totalPaid,
      remaining,
      formatDisplayDate,
      formatNumber,
      getAccountHeadName,
      getTransactionType,
      getTransactionClass,
      fetchLoan,
      router,
    };
  },
};
</script>

<style scoped>
.loan-details-container {
  max-width: 1100px;
  margin: 0 auto;
  background: #f4f6f9;
  min-height: 100vh;
  padding: 20px;
}

.section-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  padding: 16px 20px;
}

.grid-item {
  margin: 5px 0;
}

.voucher-table,
.payments-table {
  border: 1px solid #ddd;
  border-radius: 4px;
}

.voucher-table th,
.voucher-table td,
.payments-table th,
.payments-table td {
  border: 1px solid #ddd;
  padding: 8px 12px;
  font-size: 14px;
}

.voucher-table th,
.payments-table th {
  background: #f1f1f1;
  font-weight: 600;
}

.debit {
  color: green;
  font-weight: bold;
}

.credit {
  color: red;
  font-weight: bold;
}

.summary-box {
  background: #fafafa;
  padding: 12px 16px;
  border-radius: 5px;
  border: 1px solid #eee;
}
</style>
